import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import API_ENDPOINTS from '../ApiEndPoints';
import TaskToolbar from './TaskToolbar';
import TaskListView from './TaskListView';
import TaskDetailPanel from './TaskDetailPanel';
import CreateTaskModal from './CreateTaskModal';
import './taskStyles.css';

const findTaskById = (items, id) => {
    if (!Array.isArray(items)) return null;
    for (const item of items) {
        if (item.id === id) return item;
        const children = item.sub_tasks || item.subTasks;
        const found = findTaskById(children, id);
        if (found) return found;
    }
    return null;
};

// Build a nested task tree from a flat list to ensure unlimited depth
const buildTaskTree = (items) => {
    if (!Array.isArray(items)) return [];

    // If data already has nested children, leave as-is
    const alreadyNested = items.some((t) => Array.isArray(t?.sub_tasks) && t.sub_tasks.length > 0);
    if (alreadyNested) return items;

    const byId = new Map();
    const roots = [];

    items.forEach((raw) => {
        const task = { ...raw, sub_tasks: [], subTasks: [] };
        if (task.id !== undefined && task.id !== null) {
            byId.set(task.id, task);
        }
    });

    items.forEach((raw) => {
        const task = byId.get(raw.id);
        if (!task) return;
        const parentId = raw.parent_object_instances_id
            || raw.parent_id
            || raw.parentId
            || raw.parent_object_id
            || 0;

        if (parentId && byId.has(parentId)) {
            const parent = byId.get(parentId);
            parent.sub_tasks.push(task);
            parent.subTasks = parent.sub_tasks;
        } else {
            roots.push(task);
        }
    });

    return roots;
};

const TaskManagementApp = () => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        mode: 'all', // 'simple', 'structured', 'all'
        status: 'incomplete', // 'completed', 'incomplete', 'all'
        roots_only: false, // Request full list and build tree client-side
        search: '', // title/description search
        business_unit_id: '' // business unit filter (id)
    });
    const [selectedTask, setSelectedTask] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [createTaskParentId, setCreateTaskParentId] = useState(null);
    const [notice, setNotice] = useState(null);

    const [businessUnits, setBusinessUnits] = useState([]);
    const [taskDefinitions, setTaskDefinitions] = useState([]);

    const [sort, setSort] = useState({ field: 'date_due', direction: 'asc' });

    const fetchTasks = async () => {
        setLoading(true);
        setNotice(null);
        try {
            const params = { ...filters };
            const response = await axios.get(API_ENDPOINTS.tasks.index, { params });
            setTasks(buildTaskTree(response.data.data));
        } catch (error) {
            console.error("Error fetching tasks:", error);
            setNotice({ type: 'error', message: error?.response?.data?.error || 'Unable to load tasks.' });
        } finally {
            setLoading(false);
        }
    };

    const fetchBusinessUnits = async () => {
        try {
            const response = await axios.get(API_ENDPOINTS.manual.units);
            setBusinessUnits(response.data?.data || []);
        } catch (error) {
            console.error('Error fetching business units', error);
            setBusinessUnits([]);
        }
    };

    const fetchTaskDefinitions = async () => {
        try {
            const response = await axios.get(API_ENDPOINTS.tasks.definitions, { params: { manual_only: true } });
            setTaskDefinitions(response.data?.data || []);
        } catch (error) {
            console.error('Error fetching task definitions', error);
            setTaskDefinitions([]);
        }
    };

    useEffect(() => {
        fetchTasks();
    }, [filters]);

    useEffect(() => {
        fetchBusinessUnits();
        fetchTaskDefinitions();
    }, []);

    const definitionMap = useMemo(() => {
        const map = {};
        taskDefinitions.forEach((def) => {
            if (def && (def.id || def.task_definitions_id)) {
                map[def.id || def.task_definitions_id] = def;
            }
        });
        return map;
    }, [taskDefinitions]);

    const businessUnitMap = useMemo(() => {
        const map = {};
        businessUnits.forEach((unit) => {
            if (!unit) return;
            const key = unit.id ?? unit.unit_id;
            if (key !== undefined && key !== null) {
                map[String(key)] = unit;
            }
        });
        return map;
    }, [businessUnits]);

    const deriveBusinessUnitId = (task) => {
        if (!task) return '';
        const defId = task.task_definition_id || task.task_def_id || task.task_definitions_id || task.task_def?.id || task.task_definition?.id || task.object_definitions_id;
        const def = defId ? definitionMap[defId] : null;
        if (def?.unit_id) return String(def.unit_id);
        if (def?.role_id && def.role?.unit_id) return String(def.role.unit_id);

        const fromTask = task.unit_id
            || task.unitId
            || task.unit_def_id
            || task.unit?.id
            || task.unit?.unit_id
            || task.properties?.unit_id
            || task.properties?.unitid
            || task.properties?.business_unit_id
            || task.properties?.businessunit_id;

        return fromTask !== undefined && fromTask !== null && fromTask !== '' ? String(fromTask) : '';
    };

    const stripHtml = (value) => {
        if (!value) return '';
        return value.replace(/<[^>]*>?/gm, '');
    };

    const matchesSearch = (task) => {
        if (!filters.search) return true;
        const needle = filters.search.toLowerCase();
        const haystack = `${task.title || ''} ${stripHtml(task.description || '')}`.toLowerCase();
        return haystack.includes(needle);
    };

    const matchesBusinessUnit = (task) => {
        if (!filters.business_unit_id) return true;
        return deriveBusinessUnitId(task) === String(filters.business_unit_id);
    };

    const filterTree = (items) => {
        if (!Array.isArray(items)) return [];
        return items.map((task) => {
            const children = filterTree(task.sub_tasks || task.subTasks || []);
            const matchSelf = matchesSearch(task) && matchesBusinessUnit(task);
            if (matchSelf || children.length > 0) {
                return {
                    ...task,
                    sub_tasks: children,
                    subTasks: children,
                };
            }
            return null;
        }).filter(Boolean);
    };

    const filteredTasks = filterTree(tasks);

    const handleTaskClick = async (taskId) => {
        setDetailLoading(true);
        const fallback = findTaskById(tasks, taskId);
        if (fallback) {
            setSelectedTask(fallback);
        }

        try {
            const response = await axios.get(API_ENDPOINTS.tasks.detail(taskId));
            setSelectedTask(response.data.data);
        } catch (error) {
            console.error("Error fetching task details:", error);
            setNotice({ type: 'error', message: error?.response?.data?.error || 'Unable to load task details.' });
        }
        setDetailLoading(false);
    };

    const handleTaskCreated = () => {
        setIsCreateModalOpen(false);
        setCreateTaskParentId(null);
        fetchTasks();
    };

    const handleTaskUpdated = () => {
        fetchTasks();
        if (selectedTask) {
            const detailId = selectedTask.task ? selectedTask.task.id : selectedTask.id;
            if (detailId) {
                handleTaskClick(detailId); // Refresh details
            }
        }
    };

    const handleToggleComplete = async (task) => {
        const isCompleted = task.properties?.completed === 'yes';

        // Structured tasks should be completed via detail panel workflow
        if (task.object_definitions_id && !isCompleted) {
            handleTaskClick(task.id);
            return;
        }

        try {
            if (isCompleted) {
                await axios.post(API_ENDPOINTS.tasks.reopen(task.id));
            } else {
                await axios.post(API_ENDPOINTS.tasks.complete(task.id));
            }
            fetchTasks();
        } catch (error) {
            console.error('Failed to toggle completion', error);
            setNotice({ type: 'error', message: error?.response?.data?.error ?? 'Unable to update task status.' });
        }
    };

    const handleAddSubTask = (parentId) => {
        setCreateTaskParentId(parentId);
        setIsCreateModalOpen(true);
    };

    const handleSelectBusinessUnit = (unitId) => {
        setFilters(prev => ({ ...prev, business_unit_id: unitId ? String(unitId) : '' }));
    };

    return (
        <div className="task-app-shell">
            <div className="task-app-card" style={{ position: 'relative', overflow: 'hidden' }}>
                <div className="task-app-header">
                    <h3 className="task-app-title">Task Management</h3>
                    <button
                        onClick={() => { setCreateTaskParentId(null); setIsCreateModalOpen(true); }}
                        className="task-create-btn"
                    >
                        <i className="fa fa-plus"></i>
                        Create Task
                    </button>
                </div>

                {notice && (
                    <div className={`alert alert-${notice.type === 'error' ? 'danger' : 'info'}`} style={{ margin: '0 16px 12px 16px' }}>
                        {notice.message}
                    </div>
                )}

                <div className="task-toolbar-card">
                    <TaskToolbar
                        filters={filters}
                        setFilters={setFilters}
                        businessUnits={businessUnits}
                        totalTasks={filteredTasks.length}
                    />
                </div>

                <div className="task-table-wrapper">
                    {loading ? (
                        <div className="text-center" style={{ padding: '40px 0' }}>
                            <i className="fa fa-spinner fa-spin fa-3x text-muted"></i>
                            <p className="text-muted" style={{ marginTop: '10px' }}>Loading tasks…</p>
                        </div>
                    ) : (
                        <TaskListView
                            tasks={filteredTasks}
                            onTaskClick={handleTaskClick}
                            sort={sort}
                            onSortChange={setSort}
                            onTaskCreated={fetchTasks}
                            onToggleComplete={handleToggleComplete}
                            onAddSubTask={handleAddSubTask}
                            onSelectBusinessUnit={handleSelectBusinessUnit}
                            deriveBusinessUnitId={deriveBusinessUnitId}
                            businessUnitMap={businessUnitMap}
                        />
                    )}
                </div>
            </div>

            {selectedTask && (
                <div
                    className="modal show"
                    style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
                    tabIndex="-1"
                    role="dialog"
                    onClick={() => setSelectedTask(null)}
                >
                    <div
                        className="modal-dialog modal-lg"
                        role="document"
                        style={{ width: '95%', maxWidth: '1000px' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content">
                            <div className="modal-header">
                                <button
                                    type="button"
                                    className="close"
                                    onClick={() => setSelectedTask(null)}
                                    aria-label="Close"
                                >
                                    <span aria-hidden="true">&times;</span>
                                </button>
                                <h4 className="modal-title">Task Details</h4>
                            </div>
                            <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                                <TaskDetailPanel
                                    task={selectedTask}
                                    loading={detailLoading}
                                    onClose={() => setSelectedTask(null)}
                                    onUpdate={handleTaskUpdated}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isCreateModalOpen && (
                <CreateTaskModal
                    onClose={() => { setIsCreateModalOpen(false); setCreateTaskParentId(null); }}
                    onCreated={handleTaskCreated}
                    parentId={createTaskParentId}
                />
            )}
        </div>
    );
};

export default TaskManagementApp;
